import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { GestureController } from '@ionic/angular/standalone';
import type { GestureDetail } from '@ionic/core/components';

const THUMB_SELECTOR = '[data-slide-to-unlock-thumb]';
const THUMB_SIZE_PX = 52;
const THRESHOLD_RATIO = 0.85;

/**
 * Directive that adds a horizontal "slide to unlock" gesture using Ionic GestureController.
 * Place on the track container; the thumb element must have data-slide-to-unlock-thumb.
 * Emits (slideComplete) when the user slides past the threshold.
 * When [disabled] is true, the gesture is disabled and thumb is reset.
 */
@Directive({
  selector: '[appSlideToUnlock]',
  standalone: true,
})
export class SlideToUnlockDirective implements OnInit, OnDestroy {
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    if (this.gesture) {
      this.gesture.enable(!value);
      if (value && this.thumb) {
        this.thumb.style.transform = 'translateX(0px)';
      }
    }
  }
  @Output() slideComplete = new EventEmitter<void>();

  private _disabled = false;
  private gesture: ReturnType<GestureController['create']> | null = null;
  private thumb: HTMLElement | null = null;
  private maxPx = 0;
  private currentPx = 0;

  constructor(
    private el: ElementRef<HTMLElement>,
    private gestureCtrl: GestureController,
  ) {}

  ngOnInit(): void {
    const track = this.el.nativeElement;
    this.maxPx = Math.max(0, track.offsetWidth - THUMB_SIZE_PX);
    this.thumb = track.querySelector<HTMLElement>(THUMB_SELECTOR);

    this.gesture = this.gestureCtrl.create(
      {
        el: track,
        gestureName: 'slide-to-unlock',
        direction: 'x',
        threshold: 5,
        disableScroll: true,
        onStart: () => {
          if (this._disabled) return;
          this.maxPx = Math.max(0, track.offsetWidth - THUMB_SIZE_PX);
        },
        onMove: (ev: GestureDetail) => {
          if (this._disabled) return;
          this.currentPx = Math.min(Math.max(0, ev.deltaX), this.maxPx);
          if (this.thumb) this.thumb.style.transform = `translateX(${this.currentPx}px)`;
        },
        onEnd: () => {
          if (this.thumb) this.thumb.style.transform = 'translateX(0px)';
          const reached = this.currentPx >= this.maxPx * THRESHOLD_RATIO;
          this.currentPx = 0;
          if (reached && !this._disabled) this.slideComplete.emit();
        },
      },
      true,
    );
    this.gesture.enable(!this._disabled);
  }

  ngOnDestroy(): void {
    this.gesture?.destroy();
  }
}
